/**
 * NotesTab — Campus notes with sortable table, pagination, CSV export,
 *            multi-select delete, and per-row edit.
 *
 * Props:
 *   noteData      — array of note records from getcampusnotes
 *   editRights    — boolean (can edit/delete)
 *   addRights     — boolean (can add)
 *   onAddNote     — open add drawer callback
 *   onEditNote    — (noteRecord) => void — open edit drawer
 *   onDeleteNotes — (noteIds) => void — bulk delete
 *   tzOffset      — numeric timezone offset for timestamp conversion
 */

import { useState, useMemo } from 'react';
import usePersistedPerPage from '../../../../../hooks/usePersistedPerPage.js';
import Pagination from '../../../../../components/v2/Pagination.jsx';
import { CSVLink } from 'react-csv';
import { convertIsoUTCToLocal } from '../../../../../libs.jsx';
import RowActionsMenu from '../../../../../components/v2/RowActionsMenu.jsx';
import { ariaSort } from '../../../../../components/v2/SortableTh.jsx';

// ── Column definitions ──────────────────────────────────────────────────────
const COLUMNS = [
  { key: 'last_modified', label: 'Timestamp', align: 'center', sortable: true },
  { key: 'display_name', label: 'Created By', align: 'center', sortable: true },
  { key: 'note', label: 'Note', align: 'left', sortable: true },
];

const CSV_HEADERS = [
  { label: 'Note ID', key: 'note_id' },
  { label: 'Timestamp', key: 'last_modified' },
  { label: 'Created By', key: 'display_name' },
  { label: 'Note', key: 'note' },
  { label: 'Created On', key: 'create_time' },
];


// ── Sort helper ─────────────────────────────────────────────────────────────
function sortRows(data, col, dir) {
  return [...data].sort((a, b) => {
    const aVal = (a[col] ?? '').toString().toLowerCase();
    const bVal = (b[col] ?? '').toString().toLowerCase();
    if (aVal < bVal) return dir === 'asc' ? -1 : 1;
    if (aVal > bVal) return dir === 'asc' ? 1 : -1;
    return 0;
  });
}

// ── Kebab row-actions menu ───────────────────────────────────────────────────
const NoteRowActions = ({ note, onEdit, onDelete }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <>
      <RowActionsMenu label={`Actions for note`} classPrefix="dp">
          {(close) => (
              <>
                  <button
                    type="button"
                    onClick={() => { onEdit(note); close(); }}
                    className="dp-row-actions__item"
                  >
                    <svg className="dp-icon-md dp-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                    </svg>
                    Edit Note
                  </button>
                  <button
                    type="button"
                    onClick={() => { close(); setConfirmOpen(true); }}
                    className="dp-row-actions__item dp-row-actions__item--danger"
                  >
                    <svg className="dp-icon-md dp-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                    </svg>
                    Delete Note
                  </button>
              </>
          )}
      </RowActionsMenu>

      {/* Single-note delete confirmation */}
      {confirmOpen && (
        <div className="pp-dialog-backdrop" onClick={() => setConfirmOpen(false)}>
          <div className="pp-dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Delete Note">
            <div className="pp-dialog__header">
              <h2 className="pp-dialog__title">Delete Note</h2>
              <button className="dp-drawer__close" onClick={() => setConfirmOpen(false)}>{'\u2715'}</button>
            </div>
            <div className="pp-dialog__body">
              <p style={{ margin: 0, lineHeight: 1.6, color: 'var(--color-danger, #c53030)' }}>
                Are you sure you wish to delete this note?
              </p>
              <p style={{ margin: 'var(--space-2, 0.5rem) 0 0', color: 'var(--color-text-muted, #6b7280)', fontSize: 'var(--font-size-sm, 0.875rem)' }}>
                This action cannot be undone.
              </p>
            </div>
            <div className="pp-dialog__footer">
              <button className="dp-btn dp-btn--cancel" onClick={() => setConfirmOpen(false)}>Cancel</button>
              <button className="dp-btn dp-btn--danger" onClick={() => { onDelete([note.note_id]); setConfirmOpen(false); }}>
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const NotesTab = ({ noteData = [], editRights, addRights, onAddNote, onEditNote, onDeleteNotes, tzOffset = 0 }) => {
  // ── Sort state ──────────────────────────────────────────────────────────
  const [sortCol, setSortCol] = useState('last_modified');
  const [sortDir, setSortDir] = useState('desc');

  // ── Pagination ──────────────────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = usePersistedPerPage();

  // ── Multi-select ────────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  // ── Derived data ────────────────────────────────────────────────────────
  const sorted = useMemo(() => sortRows(noteData, sortCol, sortDir), [noteData, sortCol, sortDir]);
  const totalPages = Math.max(1, Math.ceil(sorted.length / perPage));
  const pageData = useMemo(() => sorted.slice((page - 1) * perPage, page * perPage), [sorted, page, perPage]);

  // Reset page when data changes
  const prevLen = useMemo(() => noteData.length, [noteData.length]);
  if (page > 1 && (page - 1) * perPage >= sorted.length) {
    // eslint-disable-next-line react-hooks/rules-of-hooks — safe: same branch always
    setTimeout(() => setPage(1), 0);
  }

  // ── Handlers ────────────────────────────────────────────────────────────
  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const toggleSelect = (noteId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(noteId)) next.delete(noteId);
      else next.add(noteId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === pageData.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pageData.map((n) => n.note_id)));
    }
  };

  const handleDeleteConfirm = () => {
    onDeleteNotes([...selectedIds]);
    setSelectedIds(new Set());
    setConfirmDeleteOpen(false);
  };

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <span className="dp-sort-icon dp-sort-icon--neutral dp-icon-xs">{'\u2195'}</span>;
    return <span className="dp-sort-icon dp-sort-icon--active dp-icon-xs">{sortDir === 'asc' ? '\u2191' : '\u2193'}</span>;
  };

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <>
      <section className="pp-section">
        {/* Header */}
        <div className="pp-section__header">
          <h3 className="pp-section__title">
            Notes
            <span className="pp-section__count">({noteData.length})</span>
          </h3>
          <div className="pp-section__actions" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {editRights && selectedIds.size > 0 && (
              <button
                type="button"
                className="dp-btn dp-btn--danger dp-btn--sm"
                onClick={() => setConfirmDeleteOpen(true)}
              >
                <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                </svg>
                Delete ({selectedIds.size})
              </button>
            )}
            {noteData.length > 0 && (
              <CSVLink
                data={noteData}
                headers={CSV_HEADERS}
                filename="campus_notes.csv"
                uFEFF={false}
                className="dp-btn dp-btn--secondary dp-btn--sm"
                style={{ textDecoration: 'none' }}
              >
                <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                Export
              </CSVLink>
            )}
            {addRights && (
              <button
                type="button"
                className="dp-btn dp-btn--primary dp-btn--sm"
                onClick={onAddNote}
              >
                <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                Add Note
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="dp-table-wrap">
          <table className="dp-table">
            <thead className="dp-thead">
              <tr>
                {editRights && (
                  <th className="dp-th dp-th--center" style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={pageData.length > 0 && selectedIds.size === pageData.length}
                      onChange={toggleSelectAll}
                      aria-label="Select all notes"
                    />
                  </th>
                )}
                {COLUMNS.map((col) => (
                  <th key={col.key} className="dp-th" style={{ textAlign: col.align }} aria-sort={col.sortable ? ariaSort(sortCol, sortDir, col.key) : undefined}>
                    {col.sortable ? (
                      <button className="dp-th__sort-btn" onClick={() => handleSort(col.key)}>
                        {col.label} <SortIcon col={col.key} />
                      </button>
                    ) : (
                      col.label
                    )}
                  </th>
                ))}
                {editRights && (
                  <th className="dp-th dp-th--center" style={{ width: '60px' }}>Edit</th>
                )}
              </tr>
            </thead>
            <tbody>
              {pageData.length === 0 ? (
                <tr className="dp-tr">
                  <td className="dp-td dp-td--center" colSpan={COLUMNS.length + (editRights ? 2 : 0)}>
                    <span className="dp-cell-text dp-cell-text--muted">No notes found</span>
                  </td>
                </tr>
              ) : (
                pageData.map((note) => (
                  <tr key={note.note_id} className="dp-tr">
                    {editRights && (
                      <td className="dp-td dp-td--center">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(note.note_id)}
                          onChange={() => toggleSelect(note.note_id)}
                          aria-label={`Select note ${note.note_id}`}
                        />
                      </td>
                    )}
                    <td className="dp-td dp-td--center" style={{ whiteSpace: 'nowrap' }}>
                      <span className="dp-cell-text">
                        {convertIsoUTCToLocal(note.last_modified, tzOffset)}
                      </span>
                    </td>
                    <td className="dp-td dp-td--center">
                      <span className="dp-cell-text">{note.display_name || '\u2014'}</span>
                    </td>
                    <td className="dp-td">
                      <span className="dp-cell-text">{note.note || ''}</span>
                    </td>
                    {editRights && (
                      <td className="dp-td dp-td--center">
                        <NoteRowActions
                          note={note}
                          onEdit={onEditNote}
                          onDelete={onDeleteNotes}
                        />
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {sorted.length > 0 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            total={sorted.length}
            perPage={perPage}
            onPageChange={setPage}
            onPerPageChange={(n) => { setPerPage(n); setPage(1); }}
            entityLabel={sorted.length === 1 ? 'note' : 'notes'}
            selectId="campus-notes-per-page"
          />
        )}
      </section>

      {/* Delete confirmation dialog */}
      {confirmDeleteOpen && (
        <div className="pp-dialog-backdrop" onClick={() => setConfirmDeleteOpen(false)}>
          <div className="pp-dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Delete Notes">
            <div className="pp-dialog__header">
              <h2 className="pp-dialog__title">Delete Notes</h2>
              <button className="dp-drawer__close" onClick={() => setConfirmDeleteOpen(false)}>{'\u2715'}</button>
            </div>
            <div className="pp-dialog__body">
              <p style={{ margin: 0, lineHeight: 1.6, color: 'var(--color-danger, #c53030)' }}>
                Are you sure you wish to delete <strong>{selectedIds.size}</strong> selected note{selectedIds.size !== 1 ? 's' : ''}?
              </p>
              <p style={{ margin: 'var(--space-2, 0.5rem) 0 0', color: 'var(--color-text-muted, #6b7280)', fontSize: 'var(--font-size-sm, 0.875rem)' }}>
                This action cannot be undone.
              </p>
            </div>
            <div className="pp-dialog__footer">
              <button className="dp-btn dp-btn--cancel" onClick={() => setConfirmDeleteOpen(false)}>Cancel</button>
              <button className="dp-btn dp-btn--danger" onClick={handleDeleteConfirm}>
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NotesTab;
